"use client";

import { PropsWithChildren } from "react";
import { CurrencyRatesInitializer } from "@/components/features/currency";
import { CustomSnackbar, NotificationProvider } from "@/components/features/notifications";
import { ApiDownWrapper } from "@/components/health/ApiDownWrapper";
import { SessionExpiredBanner } from "@/components/health/SessionExpiredBanner";
import { StaleVersionWrapper } from "@/components/health/StaleVersionWrapper";
import { TmaUnavailableWrapper } from "@/components/layout/TmaUnavailableWrapper";
import { MaintenanceWrapper } from "@/components/maintenance/MaintenanceWrapper";
import { robinhoodChain } from "@/constants/onchain-swap";
import { CorrelationProvider } from "@/contexts/CorrelationContext";
import { AuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { clientConfig } from "@/lib/config/client-config";
import { AntiBotVerificationProvider } from "@/providers/AntiBotVerificationProvider";
import { TelegramProvider, TmaRedirectProvider } from "@/utils/telegram";
import type { ConnectedWallet, User } from "@privy-io/react-auth";
import { WagmiProvider, createConfig } from "@privy-io/wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { SnackbarProvider } from "notistack";
import { defineChain } from "viem";
import { http } from "wagmi";
import { arbitrum, base, bsc, mainnet, optimism, polygon, sepolia } from "wagmi/chains";

const grvt = defineChain({
  id: 325,
  name: "GRVT",
  nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["https://rpc.grvt.io"] } },
});

const katana = defineChain({
  id: 747474,
  name: "Katana",
  nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["https://rpc.katana.network"] } },
});

type ReactQueryDevtoolsProps = {
  initialIsOpen?: boolean;
};

const ReactQueryDevtools =
  process.env.NODE_ENV === "production"
    ? (_: ReactQueryDevtoolsProps) => null
    : dynamic<ReactQueryDevtoolsProps>(
        () => import("@tanstack/react-query-devtools").then((mod) => mod.ReactQueryDevtools),
        { ssr: false },
      );

/**
 * Ethereum mainnet RPC for the reward-claim flow. viem's default mainnet public
 * RPC (eth.merkle.io) blocks CORS from the browser, so the client-side
 * estimateGas + waitForTransactionReceipt in the claim path fail for Ethereum
 * campaigns. PublicNode is CORS-open and covers the methods the claim needs.
 * TEMPORARY: replace with the authenticated API RPC proxy once it ships.
 */
const MAINNET_CLAIM_RPC_URL = "https://ethereum-rpc.publicnode.com";

/**
 * Resolve the wagmi transport for a chain:
 *  - Local-rewards-e2e harness (development/local-rewards-e2e): when
 *    NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS maps a chainId to a local anvil fork,
 *    use it so claim txs are simulated/confirmed against the fork.
 *  - Ethereum mainnet: use the CORS-open RPC above (the default would break).
 *  - Everything else: viem's default `http()` transport.
 */
const localRpcTransport = (chainId: number) => {
  const url = clientConfig.localRewardsRpcUrls?.[String(chainId)];
  if (url) return http(url);
  if (chainId === mainnet.id) return http(MAINNET_CLAIM_RPC_URL);
  return http();
};

const config = createConfig({
  chains: [mainnet, polygon, optimism, arbitrum, base, bsc, robinhoodChain, sepolia, grvt, katana],
  transports: {
    [mainnet.id]: localRpcTransport(mainnet.id),
    [polygon.id]: localRpcTransport(polygon.id),
    [optimism.id]: localRpcTransport(optimism.id),
    [arbitrum.id]: localRpcTransport(arbitrum.id),
    [base.id]: localRpcTransport(base.id),
    [bsc.id]: localRpcTransport(bsc.id),
    [robinhoodChain.id]: localRpcTransport(robinhoodChain.id),
    [sepolia.id]: localRpcTransport(sepolia.id),
    [grvt.id]: localRpcTransport(grvt.id),
    [katana.id]: localRpcTransport(katana.id),
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const selectAuthenticatedPrivyWallet = ({ wallets, user }: { wallets: ConnectedWallet[]; user: User | null }) => {
  const authenticatedWalletAddress = user?.wallet?.address?.toLowerCase();

  if (!authenticatedWalletAddress) {
    return wallets[0];
  }

  return wallets.find((wallet) => wallet.address.toLowerCase() === authenticatedWalletAddress);
};

export function Providers({ children }: PropsWithChildren) {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <CurrencyRatesInitializer />
      <CorrelationProvider>
        <TmaRedirectProvider>
          <AntiBotVerificationProvider>
            <TelegramProvider>
              <AuthenticationProvider>
                <WagmiProvider config={config} setActiveWalletForWagmi={selectAuthenticatedPrivyWallet}>
                  <SnackbarProvider
                    Components={{
                      success: CustomSnackbar,
                      error: CustomSnackbar,
                      info: CustomSnackbar,
                      trade: CustomSnackbar,
                      warning: CustomSnackbar,
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    dense
                    classes={{ containerRoot: "z-alert" }}
                  >
                    <NotificationProvider>
                      <TmaUnavailableWrapper>
                        <MaintenanceWrapper>
                          <ApiDownWrapper>
                            <StaleVersionWrapper>{children}</StaleVersionWrapper>
                          </ApiDownWrapper>
                          {/* After the update and API notices: same z tier, later in the DOM, so the session notice paints over it. */}
                          <SessionExpiredBanner />
                        </MaintenanceWrapper>
                      </TmaUnavailableWrapper>
                    </NotificationProvider>
                  </SnackbarProvider>
                </WagmiProvider>
              </AuthenticationProvider>
            </TelegramProvider>
          </AntiBotVerificationProvider>
        </TmaRedirectProvider>
      </CorrelationProvider>
    </QueryClientProvider>
  );
}
